import { useEffect } from "react";

import { Drawer as MagneticDrawer } from "@magnetic/drawer";
import { useClickOutside } from "@magnetic/hooks";

type MagneticProps = React.ComponentProps<typeof MagneticDrawer>;
/**
 * To avoid event-type mismatches throughout the app, normalize the
 * wrapper's onClose prop to a no-arg function.
 */
type MagneticOnClose = MagneticProps["onClose"];

/**
 * Wrapper props:
 * - Magnetic Drawer props (remove 'onClose' so we can redefine it)
 * - Optional closing behavior props
 * - Normalized close callback
 */
export type DrawerProps = Omit<MagneticProps, "onClose"> & {
  /** Close drawer when clicking outside (default: true) */
  readonly closeOnOutsideClick?: boolean;
  /** Close drawer when pressing Escape (default: true) */
  readonly closeOnEscape?: boolean;
  readonly onClose?: () => void;
  readonly "data-qa"?: string;
};

/**
 * App-level Drawer wrapper to allow for consistent closing behavior
 * with escape key and outside click. If the drawer has a close button,
 * it will still work.
 */
export const Drawer = ({
  isOpen = false,
  onClose,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  children,
  "data-qa": dataQa,
  ...rest
}: DrawerProps) => {
  /**
   * Escape Key Handler
   *
   * Magnetic drawer does have escapeKey set to true, but it
   * can be unreliable if focus isn't in the drawer. Added a
   * document-level listener to ensure escape closes consistently
   */
  useEffect(() => {
    // If drawer isn't open, Escape close isn't enabled, or there's no
    // onClose callback, do nothing and return 'undefined'
    if (!isOpen || !closeOnEscape || !onClose) {
      return undefined;
    }

    // Close drawer on escape key press
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    // Attach listener while drawer is open
    document.addEventListener("keydown", handleKeyDown, true);
    // Cleanup on close/unmount to avoid leaks
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen, closeOnEscape, onClose]);

  /**
   * Outside Click Handler (ref-based)
   *
   * Magnetic drawer hardcodes outsidePress: false, so we can't enable
   * "native" outside press close via props
   *
   * Magnetic's useClickOutside hook adds a document listener and calls
   * the callback when the click occurs outside the referenced node.
   */
  const drawerRef = useClickOutside<HTMLDivElement>((e) => {
    if (!closeOnOutsideClick || !onClose || !isOpen) return;

    const target = e.target as Node;

    if (target instanceof Element) {
      const isInPortal =
        target.closest('[role="listbox"]') ||
        target.closest('[role="menu"]') ||
        target.closest('[role="dialog"]') ||
        target.closest(".magentic-popover") ||
        target.closest(".magentic-tooltip") ||
        target.closest("[data-portal]");
      if (isInPortal) return;
    }

    onClose();
  });

  return (
    <MagneticDrawer
      isOpen={isOpen}
      /**
       * Magnetic's onClose is typed to accept a React mouse event,
       * so added casting because the types don't match
       */
      onClose={
        (onClose ? () => onClose() : undefined) as unknown as MagneticOnClose
      }
      {...rest}
    >
      {/* Need a DOM node to attach 'drawerRef' to for click-outside detection.
       * 'display: contents' keeps the node in the DOM but removes its own
       * layout so its children behave as if they are direct children of the
       * drawer.
       */}
      <div ref={drawerRef} style={{ display: "contents" }} data-qa={dataQa}>
        {children}
      </div>
    </MagneticDrawer>
  );
};

// Re-export subcomponents so we can keep using them as is.
Drawer.Heading = MagneticDrawer.Heading;
Drawer.Content = MagneticDrawer.Content;
Drawer.Footer = MagneticDrawer.Footer;
Drawer.Divider = MagneticDrawer.Divider;
